function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if(summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return; // 9.0.0: if (summary.closest('header-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch(e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if(navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

   window.addEventListener(
    'focus', 
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    }, 
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
       button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }
    
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

/*
 * Shopify Common JS
 *
 */
if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  }
};

Shopify.setSelectorByValue = function(selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for(var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if(event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if(!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if(isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches 
          ? addTrapFocus() 
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach(submenu => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) 
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset = 
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    
    document.documentElement.style.setProperty(
      '--header-bottom-position', 
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header && 
      document.documentElement.style.setProperty(
        '--header-bottom-position', 
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}

customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    /* PB */
    this.dragging = false;
    this.dragTimeout = null;
    this.dragPosInitial = 0;
    this.dragPosFinal = 0;
    this.dragThreshold = 50;
    /* /PB */

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver(entries => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    /* PB */
    this.slider.addEventListener('mousedown', this.dragStart.bind(this));
    this.handleLinks();
    /* /PB */
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', { 
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1]
          }
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = 
      event.currentTarget.name === 'next' 
        ? this.slider.scrollLeft + step * this.sliderItemOffset 
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }

  /* PB */
  dragStart(event) {
    event = event || window.event;

    if (event.type != 'touchstart') {
      event.preventDefault();
      clearTimeout(this.dragTimeout);

      this.dragging = false;
      this.dragPosInitial = this.dragPosX1 = event.clientX;

      document.onmousemove = this.dragAction.bind(this);
      document.onmouseup = this.dragEnd.bind(this);
    }
  }

  dragAction (event) {
    this.dragging = true;
  }

  dragEnd (event) {
    event = event || window.event;
    event.preventDefault();

    this.dragPosFinal = event.clientX;

    const moveLength = Math.abs(this.dragPosFinal - this.dragPosInitial);
    const slidesToMove = Math.round(moveLength / this.sliderItemOffset);

    if ((moveLength >= this.dragThreshold) && slidesToMove) {
      for(let i = 0; i < slidesToMove; i++) {
        if (this.dragPosFinal < this.dragPosInitial) {
          this.nextButton.dispatchEvent(new Event('click'));
        } else if (this.dragPosFinal > this.dragPosInitial) {
          this.prevButton.dispatchEvent(new Event('click'));
        }
      }
    }

    document.onmouseup = null;

    let _this = this;

    this.dragTimeout = setTimeout(() => {
      _this.dragging = false;
    }, 300);
  }

  handleLinks() {
    if (!this.sliderLinks || !this.sliderLinks.length) {
      return;
    }

    let _this = this;

    this.sliderLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        if (_this.dragging) {
          event.stopImmediatePropagation();
          event.preventDefault();
        }
      });
    });
  }
  /* /PB */
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach(link => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;
    
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition = 
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }
    
    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach(link => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition = 
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;
    
    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);


class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);

      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);


class ProductRecommendations extends HTMLElement {
  observer = undefined;
  
  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

/**********************************************************/
/*             Platinum Born customizations               */
/**********************************************************/

class VideoWrapper extends HTMLElement {
  constructor() {
    super();

    this.video = this.querySelector('video');
    this.playControl = this.querySelector('button.video-wrapper__play-control');
    this.audioControl = this.querySelector('button.video-wrapper__audio-control');
    this.videoPlaying = false;
    
    if (!this.video || (!this.playControl && !this.audioControl)) return;

    // this.videoLoadHandle.bind(this);

    if (this.playControl) {
      this.playControl.addEventListener('click', this.playControlHandle.bind(this));
    };

    if (this.audioControl) {
      this.audioControl.addEventListener('click', this.audioControlHandle.bind(this));
    };
  }

  connectedCallback() /*videoLoadHandle()*/ {
    if (this.video.defaultMuted) {
      this.video.muted = true;
      this.classList.remove('video-wrapper--audio-on');
    }

    if (this.video.getAttribute('autoplay')) {
      // force autoplay for safari
      this.video.play();
      this.videoPlaying = true;
      this.classList.add('video-wrapper--video-playing');
  
      if (!this.video.defaultMuted) {
        this.video.muted = false;
        this.classList.add('video-wrapper--audio-on');
      }
    }

    if (this.playControl) {
      this.playControl.classList.remove('hidden');
    }

    if (this.audioControl) {
      this.audioControl.classList.remove('hidden');
    }
  }

  playControlHandle() {
    if (this.videoPlaying) {
      this.video.pause();
      this.videoPlaying = false;
      this.classList.remove('video-wrapper--video-playing');
    } else {
      this.video.play();
      this.videoPlaying = true;
      this.classList.add('video-wrapper--video-playing');
    }
  }

  audioControlHandle() {
    if (this.video.muted) {
      this.video.muted = false;
      this.classList.add('video-wrapper--audio-on');
    } else {
      this.video.muted = true;
      this.classList.remove('video-wrapper--audio-on');
    }
  }
}

customElements.define('video-wrapper', VideoWrapper);


// by Chris Coyier @ https://css-tricks.com/how-to-animate-the-details-element/
class DetailsAccordion {
  constructor(el) {
    // Store the <details> element
    this.el = el;
    // Store the <summary> element
    this.summary = el.querySelector('summary');
    // Store the <div class="content"> element
    this.content = this.summary.nextElementSibling;

    // Store the animation object (so we can cancel it if needed)
    this.animation = null;
    // Store if the element is closing
    this.isClosing = false;
    // Store if the element is expanding
    this.isExpanding = false;
    // Detect user clicks on the summary element
    this.summary.addEventListener('click', (e) => this.onClick(e));
  }

  onClick(e) {
    // Stop default behaviour from the browser
    e.preventDefault();
    // Add an overflow on the <details> to avoid content overflowing
    this.el.style.overflow = 'hidden';
    // Check if the element is being closed or is already closed
    if (this.isClosing || !this.el.open) {
      // close other opened elements from a group if any
      if (this.el.dataset.group) {
        const elementsToClick = document.querySelectorAll('details[data-group="'+ this.el.dataset.group +'"][open]:not(.shrinking) > summary');

        if (elementsToClick.length) {
          elementsToClick.forEach((summary) => {
            summary.dispatchEvent(new Event('click', {bubbles: true, cancelable: true}));
          });
        }
      }
      
      this.open();
    // Check if the element is being openned or is already open
    } else if (this.isExpanding || this.el.open) {
      this.shrink();
    }
  }

  shrink() {
    // Set the element as "being closed"
    this.el.classList.add('shrinking');
    this.isClosing = true;
    
    // Store the current height of the element
    const startHeight = `${this.el.offsetHeight}px`;
    // Calculate the height of the summary
    const endHeight = `${this.summary.offsetHeight}px`;
    
    // If there is already an animation running
    if (this.animation) {
      // Cancel the current animation
      this.animation.cancel();
    }
    
    // Start a WAAPI animation
    this.animation = this.el.animate({
      // Set the keyframes from the startHeight to endHeight
      height: [startHeight, endHeight]
    }, {
      duration: 500,
      easing: 'linear'
    });
    
    // When the animation is complete, call onAnimationFinish()
    this.animation.onfinish = () => this.onAnimationFinish(false);
    // If the animation is cancelled, isClosing variable is set to false
    this.animation.oncancel = () => this.isClosing = false;
  }

  open() {
    // Apply a fixed height on the element
    this.el.style.height = `${this.el.offsetHeight}px`;
    // Force the [open] attribute on the details element
    this.el.open = true;
    // Wait for the next frame to call the expand function
    window.requestAnimationFrame(() => this.expand());
  }

  expand() {
    // Set the element as "being expanding"
    this.el.classList.add('expanding');
    this.isExpanding = true;
    // Get the current fixed height of the element
    const startHeight = `${this.el.offsetHeight}px`;
    // Calculate the open height of the element (summary height + content height)
    const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;
    
    // If there is already an animation running
    if (this.animation) {
      // Cancel the current animation
      this.animation.cancel();
    }
    
    // Start a WAAPI animation
    this.animation = this.el.animate({
      // Set the keyframes from the startHeight to endHeight
      height: [startHeight, endHeight]
    }, {
      duration: 500,
      easing: 'linear'
    });
    // When the animation is complete, call onAnimationFinish()
    this.animation.onfinish = () => this.onAnimationFinish(true);
    // If the animation is cancelled, isExpanding variable is set to false
    this.animation.oncancel = () => this.isExpanding = false;
  }

  onAnimationFinish(open) {
    // Set the open attribute based on the parameter
    this.el.classList.remove('shrinking');
    this.el.classList.remove('expanding');
    this.el.open = open;
    // Clear the stored animation
    this.animation = null;
    // Reset isClosing & isExpanding
    this.isClosing = false;
    this.isExpanding = false;
    // Remove the overflow hidden and the fixed height
    this.el.style.height = this.el.style.overflow = '';
  }
}

function initDetailsAnimations() {
  const animatedDetails = document.querySelectorAll('details.details-animated, details[data-group]');

  if (animatedDetails.length) {
    animatedDetails.forEach((details) => {
      new DetailsAccordion(details);
    });
  }
}


function initCustomWiserSlider() {
  const wiserMobileSliderWrapper = document.querySelectorAll('.wiser-mobile-slider .apps-wrapper .wise-product-section #presets2.evm-wsrow');

  if (wiserMobileSliderWrapper.length) {
    let winWidth, resizeTimeout;
    
    wiserMobileSliderWrapper.forEach((wiserRow) => {
      const boxes = wiserRow.querySelectorAll('.ws-box-wrapper');
      const section = wiserRow.closest('.shopify-section');

      if (boxes.length) {
        const options = {
          loop: false,
          slidesPerView: 'auto',
          loopedSlides: 1,
          rewind: true,
          spaceBetween: 1,
          speed: 500,
          freeMode: {
            enabled: true,
            sticky: true,
          },
          centeredSlides: false,
          shortSwipes: false,
          longSwipes: true,
          slideToClickedSlide: false,
          pagination: false,
          navigation: false,
          grabCursor: true,
        };
        
        wiserRow.parentElement.classList.add('swiper');
        wiserRow.classList.add('swiper-wrapper');

        boxes.forEach((wiserBox) => {
          wiserBox.classList.add('swiper-slide');
        });

        let swiper = new Swiper('#' + section.id + ' .wiser-mobile-slider .apps-wrapper .wise-product-section', options);

        let swiperState = 1;
        winWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

        if (winWidth > 750) {
          swiper.destroy();
          swiperState = 0;
        }

        window.addEventListener('resize', () => {
          clearTimeout(resizeTimeout);
          
          resizeTimeout = setTimeout(() => {
            winWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    
            if (winWidth > 750) {
              if (swiperState) {
                swiperState = 0;
                swiper.destroy(false, false);
              }
            } else {
              if (!swiperState) {
                swiperState = 1;
                swiper = new Swiper('#' + section.id + ' .wiser-mobile-slider .apps-wrapper .wise-product-section', options);
              }
            }
          }, 300);
        });
      }
    });
  }
}


function initSectionAnimations() {
  let wow = new WOW({
    offset: ((window.pb_animations && window.pb_animations.offset) ? window.pb_animations.offset : 150),
    animateClass: 'animate__animated'
  });
  wow.init();
}


function pageAnchorsSmoothScroll() {
  document.body.addEventListener("click", function(e) {
	if (e.target && e.target.nodeName == "A" && e.target.href && e.target.href.lastIndexOf('#') != -1) {
      let linkParts = e.target.href.split('#');

      if (!linkParts[1]) {
        return;
      }

      const scrollTarget = document.getElementById(linkParts[1]);

      if (!scrollTarget) {
        return;
      }

      e.preventDefault();

      window.setTimeout(() => {
        scrollTarget.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });

        let currentHashIndex = window.location.href.lastIndexOf('#');
        let newURL = window.location.href;

        if (currentHashIndex != -1) {
          newURL = window.location.href.replace(window.location.href.slice(currentHashIndex + 1), linkParts[1]);
        } else {
          newURL += '#'+ linkParts[1];
        }

        window.history.pushState({}, document.getElementsByTagName("title")[0].innerHTML +' #'+ linkParts[1] , newURL);
      });
	}
  });
}


function videoAutoplayForSafari() {
  const videos = document.querySelectorAll('video[autoplay]');

  if (videos.length) {
    videos.forEach((video) => {
      video.play();
      if (video.parentElement.classList.contains('video-wrapper')) {
        video.parentElement.classList.add('video-wrapper--video-playing');
        document.activeElement?.blur();
      }
    });
  }
}


function handleHotspots(event) {
  let winWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

  if (
    (event.target.classList.contains('shoppable-image__hotspot-inner') || event.target.classList.contains('shoppable-image__hotspot')) && 
    winWidth <= 800
  ) {
    deactivateHotspots();

    if (event.target.classList.contains('shoppable-image__hotspot-inner')) {
      event.target.parentElement.classList.add('active');
    } else {
      event.target.classList.add('active');
    }
  } else {
    deactivateHotspots();
  }
}

function deactivateHotspots() {
  const activeHotspots = document.querySelectorAll('.shoppable-image__hotspot.active');

  if (activeHotspots.length) {
    activeHotspots.forEach((hotspot) => {
      hotspot.classList.remove('active');
    });
  }
}

function hotspotHoverFix() {
  document.addEventListener('touchend', (event) => {
    handleHotspots(event);
  });

  document.addEventListener('click', (event) => {
    handleHotspots(event);
  });
}


function addStylesToComparisonTablesShadowDom (element, attempt = 0) {
  if (attempt > 3) {
    return;
  }
  //console.log('addStylesToComparisonTablesShadowDom');

  //console.log('addStylesToComparisonTablesShadowDom element');
  //console.log(element);

  //console.log('addStylesToComparisonTablesShadowDom element.shadowRoot');
  //console.log(element.shadowRoot);
  
  if (
    typeof element.shadowRoot !== 'undefined' && 
    typeof element.shadowRoot === 'object' && 
    element.shadowRoot !== null
  ) {
    const ribbon = element.shadowRoot.querySelector('.commonninja-ribbon');
    
    //console.log('addStylesToComparisonTablesShadowDom shadowRoot ribbon element');
    //console.log(ribbon);
    
    if (ribbon) {
      ribbon.remove();
      //console.log('addStylesToComparisonTablesShadowDom removed ribbon element out of shadowRoot');
    }

    const css = 
`.commonninja-ribbon { display: none !important; height: 0 !important; margin: 0 !important; overflow: hidden !important; }
.plugin-wrapper { font-family: EuclidCircularA, sans-serif !important; font-weight: 400; font-size: 1.6rem; line-height: 1.6; color: #141a25 !important; }
.plugin-wrapper div #tableWrapper { margin: 0 auto; }
.plugin-wrapper div #tableWrapper .main-table-wrapper { border: none !important; }
.plugin-wrapper div #tableWrapper #vsTable { font-size: 1.6rem; text-align: left; }
.plugin-wrapper div #tableWrapper #vsTable p { line-height: 1.6; }
.plugin-wrapper div #tableWrapper.classic #vsTable { border: none; color: #141A25 !important; }
.plugin-wrapper div #tableWrapper #vsTable tr { height: auto; border: none; }
.plugin-wrapper div #tableWrapper #vsTable thead tr { font-size: 3rem !important; color: #141A25 !important; }
.plugin-wrapper div #tableWrapper.classic #vsTable tbody tr { border: none; background: transparent; font-size: 1.6rem !important; color: #141A25 !important; }
.plugin-wrapper div #tableWrapper.classic #vsTable thead tr { border: none; font-family: "minerva-modern", sans-serif; font-weight: 400; }
.plugin-wrapper div #tableWrapper #vsTable td, .plugin-wrapper div #tableWrapper #vsTable th { padding: 2.7rem 2% 2.7rem 3.5%; }
.plugin-wrapper div #tableWrapper.classic #vsTable { border:none !important; }
.plugin-wrapper div #tableWrapper.classic #vsTable thead th { color: #141a25 !important; }
.plugin-wrapper div #tableWrapper.classic #vsTable td { color: #141A25 !important; }
.plugin-wrapper div #tableWrapper.classic #vsTable .cat.title { background-color: transparent !important; padding: 0; width: 130px !important; width: max(100px, 12%) !important; }
.plugin-wrapper div #tableWrapper.classic #vsTable tbody .cat { background: transparent !important; padding-left: 0; padding-right: 1%; font-family: "minerva-modern", sans-serif; font-weight: 400; font-size: 2rem !important; }
.plugin-wrapper div #tableWrapper.classic #vsTable tbody td:first-child { border-left:none; }
.plugin-wrapper div #tableWrapper.classic #vsTable tbody td { border-bottom: 1px solid #141A25; }
.plugin-wrapper div #tableWrapper.classic #vsTable thead th { width: 29% !important;  font-size: 3rem !important; line-height: 1; font-weight: 400; color: #141A25 !important; background: transparent; border-bottom: 1px solid #141A25; }
.plugin-wrapper div #tableWrapper.classic #vsTable th:last-child, .plugin-wrapper div #tableWrapper.classic #vsTable td:last-child { border-right: 0; }
.plugin-wrapper div #tableWrapper.classic #vsTable thead div,
.plugin-wrapper div #tableWrapper.classic #vsTable thead div p { line-height: 1; }
@media only screen and (max-width: 989px) {
  .plugin-wrapper div #tableWrapper #vsTable td, .plugin-wrapper div #tableWrapper #vsTable th { padding: 2rem; }
  .plugin-wrapper div #tableWrapper.classic #vsTable thead th { width: 33.3333% !important; }
}

@media only screen and (max-width: 812px) {
  .plugin-wrapper div #tableWrapper .main-table-wrapper { width: 140%; }
}

@media only screen and (max-width: 639px) {
  .plugin-wrapper div #tableWrapper #vsTable td, .plugin-wrapper div #tableWrapper #vsTable th { padding: 1.5rem; }
  .plugin-wrapper div #tableWrapper .main-table-wrapper { width: 190%; }
}

@media only screen and (max-width: 459px) {
  .plugin-wrapper div #tableWrapper .main-table-wrapper { width: 240%; }
}

@media only screen and (max-width: 375px) {
  .plugin-wrapper div #tableWrapper .main-table-wrapper { width: 290%; }
}
`;
    
    let style = document.createElement('style');
     
    style.innerHTML = css;
    element.shadowRoot.appendChild(style);
    
    /*const sheet = new CSSStyleSheet();

    sheet.replaceSync(css);
    element.shadowRoot.adoptedStyleSheets.push(sheet);*/
              
    //console.log('addStylesToComparisonTablesShadowDom appended style to shadowRoot');
  } else {
    console.log('addStylesToComparisonTablesShadowDom shadowRoot doesn\'t exist');
    
    attempt++;
    
    setTimeout(function(){
      addStylesToComparisonTablesShadowDom (element, attempt);
    }, 500);
  }
}


function runComparisonTablesMutationObserver (element) {
  //let index = 'desktop';

  //if (element.id == 'comparison-table-mobile') {
    //index = 'mobile';
  //}
  
  //console.log('addStylesToShadowDom ' + index);
  
  const config = { childList: true, subtree: true };
  
  const callback = (mutationList, observer) => {
    //console.log('addStylesToShadowDom ' + index + ' MutationObserver callback');
    
    for (const mutation of mutationList) {
      //console.log('addStylesToShadowDom ' + index + ' MutationObserver callback mutation');
      //console.log(mutation);
      
      if (mutation.type === "childList" && mutation.addedNodes.length) {
        //console.log('addStylesToShadowDom ' + index + ' MutationObserver callback mutation.type === "childList" && mutation.addedNodes.length > 0');
        
        mutation.addedNodes.forEach((addedNode) => {
          //console.log('addStylesToShadowDom ' + index + ' MutationObserver callback mutation addedNode');
          //console.log(addedNode);
          
          if (addedNode.classList.contains('commonninja_component')) {
            //console.log('addStylesToShadowDom ' + index + ' MutationObserver callback mutation addedNode has class "commonninja_component" and shadowRoot');
            addStylesToComparisonTablesShadowDom(addedNode);
            
            observer.disconnect();
            //console.log('addStylesToShadowDom ' + index + ' MutationObserver callback observer disconnected');
          }
        });
      }
    }
  };
  
  const observer = new MutationObserver(callback);
  
  observer.observe(element, config);
}


function comparisonTablesFix() {
  const comparisonTableDesktop = document.getElementById('comparison-table-desktop');
  const comparisonTableMobile = document.getElementById('comparison-table-mobile');

  let commonninjaComponent = null;
  
  if (comparisonTableDesktop) {
    //console.log('comparisonTableDesktop');
    //console.log(comparisonTableDesktop);

    commonninjaComponent = comparisonTableDesktop.querySelector('.commonninja_component');

    if (commonninjaComponent) {
      addStylesToComparisonTablesShadowDom(commonninjaComponent);
    } else {
      runComparisonTablesMutationObserver(comparisonTableDesktop);
    }
  }
  
  if (comparisonTableMobile) {
    //console.log('comparisonTableMobile');
    //console.log(comparisonTableMobile);
    
    commonninjaComponent = comparisonTableMobile.querySelector('.commonninja_component');

    if (commonninjaComponent) {
      addStylesToComparisonTablesShadowDom(commonninjaComponent);
    } else {
      runComparisonTablesMutationObserver(comparisonTableMobile);
    }
  }
}


function pageAnchorScrollOnLoad() {
  if (window.location.hash) {
    window.scroll(0, 0);

    window.addEventListener("load", (event) => {
      let scrollTarget = document.querySelector(window.location.hash);

      if (scrollTarget) {
        window.setTimeout(() => {
          scrollTarget.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }, 300);
      }
    });
  }
}


function quoteURLsFix() {
  //document.addEventListener("DOMContentLoaded", () => {
    const element = document.querySelector('body.domain--is-wholesale');
    
    if (!element) return;
    
    //console.log(element);
    
    const config = { childList: true, subtree: true };

    const replaceLinkURL = (link) => {
      let url = link.getAttribute('href');
                
      //console.log('url:' + url);
      
      if (url) {
        url = url.replace('platinum-born.myshopify.com', 'platinumbornpartners.com');
        
        //console.log('url:' + url);
        
        link.setAttribute('href', url);
        
        //console.log('link with new url:');
        //console.log(link);
      }
    }

    const replaceImgSrc = (img) => {
      let src = img.getAttribute('src');
              
      //console.log('src: ' + src);
      
      if (src) {
        if (src.indexOf('http') !== 0) {
          src = window.location.protocol + src;
        }
        
        let url = new URL(src);
        
        //console.log('src url: ');
        //console.log(url);

        if (url.searchParams.get('width') == 800) return;
        
        url.searchParams.set('width', 800);
        url.searchParams.set('height', 800);
        
        //console.log('new src url: ');
        //console.log(url);
        
        img.setAttribute('src', url.toString());
        
        //console.log('img with new src: ');
        //console.log(img);
      }
    }
    
    const callback = (mutationList, observer) => {
      //console.log('body observer callback');
      
      //console.log('mutationList: ');
      //console.log(mutationList);
      
      for (const mutation of mutationList) {
        //console.log('mutation: ');
        //console.log(mutation);
        
        if (mutation.type === "childList" && mutation.addedNodes.length) {
          mutation.addedNodes.forEach((addedNode) => {
            //console.log('addedNode: ');
            //console.log(addedNode);

            if (addedNode.nodeType !== Node.ELEMENT_NODE) return;

            if ((addedNode.hasAttribute('id') && addedNode.getAttribute('id') == 'rfq_popup_form') || addedNode.classList.contains('grfq__popupForm__modal')) {
              let productLinks = addedNode.querySelectorAll('.grfq__popupForm__itemTitle a');
              let productThumbs = addedNode.querySelectorAll('.grfq__popupForm__itemThumbImg');

              //console.log('productLinks: ');
              //console.log(productLinks);

              //console.log('productThumbs: ');
              //console.log(productThumbs);

              if (productLinks.length) {
                productLinks.forEach((link) => {
                  replaceLinkURL(link);
                });
              }

              if (productThumbs.length) {
                productThumbs.forEach((productThumb) => {
                  replaceImgSrc(productThumb);
                });
              }
            }
        
            if (addedNode.classList.contains('grfq__popupForm__itemTitle')) {
              let link = addedNode.querySelector('a');
              
              //console.log('link: ');
              //console.log(link);
              
              if (link) {
                replaceLinkURL(link);
              }
            } else if (addedNode.classList.contains('grfq__popupForm__itemThumbImg')) {
              //console.log('img: ');
              //console.log(addedNode);
              
              replaceImgSrc(addedNode);
            }
          });
        }
      }
    };
    
    const observer = new MutationObserver(callback);
    
    observer.observe(element, config);
  //});
}

function hidePopupWholesale(){
  if (window.location.hostname.toLowerCase().includes('platinumbornpartners.com')) {
    const hidePopup = () => {
      const popup = document.querySelector('div[role="dialog"][aria-modal="true"][aria-label="POPUP Form"]');
      if (popup) {
          popup.style.display = 'none';
          // O también puedes usar: popup.remove(); para eliminarlo completamente
      }
    }

    const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
            if (mutation.type === 'childList') {
                hidePopup();
            }
        });
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

    hidePopup();
  }else{
    console.log('no ws')
  }
}


document.addEventListener("DOMContentLoaded", () => {
  if (
    typeof window.pb_animations === 'object' && !Array.isArray(window.pb_animations) && window.pb_animations !== null && 
    typeof window.pb_animations.use_animations && window.pb_animations.use_animations === true
  ) {
    // NOTE: doesn't work well with the site loading optimization and script concatenation
    // initSectionAnimations();
  }
  //pageAnchorScrollOnLoad();
  pageAnchorsSmoothScroll();
  initDetailsAnimations();
  videoAutoplayForSafari();
  hotspotHoverFix();
  quoteURLsFix();
  hidePopupWholesale();
});


window.addEventListener("load", (event) => {
  initCustomWiserSlider();
  comparisonTablesFix();
});